import { undo, redo } from 'prosemirror-history';
import { wrapInList, liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list';
import { AllSelection, NodeSelection, TextSelection, Selection } from 'prosemirror-state';
import { toggleMark,
  chainCommands,
  deleteSelection,
  joinBackward,
  selectNodeBackward,
  joinForward,
  selectNodeForward,
  newlineInCode,
  createParagraphNear,
  liftEmptyBlock,
  splitBlock,
  exitCode,
  selectAll,
  wrapIn,
  setBlockType,
 } from 'prosemirror-commands';
import dryEditorClass from './DryEditorClass';
import schema from './schema';
import constants from '.././../constants';
const { EDITOR_COMMAND_NAME_MAP } = constants;
import logger from '../../logger';

// Returns the from/to positions of a mark around a resolved position, or null
const getMarkRange = ($pos, markType) => {
  const parent = $pos.parent;
  const start = $pos.start();
  let from = $pos.pos;
  let to = $pos.pos;
  let found = false;

  parent.forEach((child, offset) => {
    if (!child.isText) return;
    const childFrom = start + offset;
    const childTo = childFrom + child.nodeSize;
    if (child.marks.some(m => m.type === markType)) {
      if (childFrom <= $pos.pos && $pos.pos <= childTo) {
        from = childFrom;
        to = childTo;
        found = true;
      }
    }
  });

  return found ? { from, to } : null;
};

const sharedToggleMark = (mark, attrs = {}) => {
  return toggleMark(mark, attrs)(dryEditorClass.getEditorState(), dryEditorClass.getDispatch());
};

const createNodeElement = ({node, options = {}}, inherit = false) => {
  const dispatch = dryEditorClass.getDispatch();
  const state = dryEditorClass.getEditorState();
  const nodeItem = schema.nodes[node];
  
  dispatch(state
    .tr
    .replaceSelectionWith(nodeItem.create(options), inherit)
    .scrollIntoView()
  );
}

const editorCommands = {
  [EDITOR_COMMAND_NAME_MAP.enter]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const view = dryEditorClass.getEditorView();
    return chainCommands(newlineInCode, createParagraphNear, liftEmptyBlock, splitBlock)(state, dispatch, view);
  },
  [EDITOR_COMMAND_NAME_MAP.exitCode]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    return exitCode(state, dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.backspace]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const view = dryEditorClass.getEditorView();
    return chainCommands(deleteSelection, joinBackward, selectNodeBackward)(state, dispatch, view);
  },
  [EDITOR_COMMAND_NAME_MAP.mod_backspace]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const view = dryEditorClass.getEditorView();
    return chainCommands(deleteSelection, joinBackward, selectNodeBackward)(state, dispatch, view);
  },
  [EDITOR_COMMAND_NAME_MAP.delete]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const view = dryEditorClass.getEditorView();
    return chainCommands(deleteSelection, joinForward, selectNodeForward)(state, dispatch, view);
  },
  [EDITOR_COMMAND_NAME_MAP.mod_delete]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const view = dryEditorClass.getEditorView();
    return chainCommands(deleteSelection, joinForward, selectNodeForward)(state, dispatch, view);
  },
  [EDITOR_COMMAND_NAME_MAP.select_all]: () => {
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    return selectAll(state, dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.bold]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.bold}`);
    return sharedToggleMark(schema.marks.strong);
  },
  [EDITOR_COMMAND_NAME_MAP.italic]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.italic}`);
    sharedToggleMark(schema.marks.em);
  },
  [EDITOR_COMMAND_NAME_MAP.underline]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.underline}`);
    sharedToggleMark(schema.marks.underline);
  },
  [EDITOR_COMMAND_NAME_MAP.code]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.code}`);
    sharedToggleMark(schema.marks.code);
  },
  [EDITOR_COMMAND_NAME_MAP.subscript]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.subscript}`);
    sharedToggleMark(schema.marks.subscript);
  },
  [EDITOR_COMMAND_NAME_MAP.superscript]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.superscript}`);
    sharedToggleMark(schema.marks.superscript);
  },
  [EDITOR_COMMAND_NAME_MAP.strikethrough]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.strikethrough}`);
    sharedToggleMark(schema.marks.strikethrough);
  },
  [EDITOR_COMMAND_NAME_MAP.font_color]: (attrs = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.font_color}`);
    const dispatch = dryEditorClass.getDispatch();
    let state = dryEditorClass.getEditorState();
    const fontColor = schema.marks.fontcolor;

    let { ranges } = state.selection;
    let hasColor = false;
    for (let i = 0; !hasColor && i < ranges.length; i++) {
      let { $from, $to } = ranges[i];
      hasColor = state.doc.rangeHasMark($from.pos, $to.pos, fontColor);
    }
    for (let i = 0; i < ranges.length; i++) {
      let {$from, $to} = ranges[i]
      if (hasColor) {
        dispatch(state.tr.removeMark($from.pos, $to.pos, fontColor));
      }

      // Always run to add the latest font color with the latest state
      state = dryEditorClass.getEditorState();
      dispatch(state.tr.addMark($from.pos, $to.pos, fontColor.create(attrs)));
    }
  },
  [EDITOR_COMMAND_NAME_MAP.background_color]: (attrs = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.background_color}`);
    const dispatch = dryEditorClass.getDispatch();
    let state = dryEditorClass.getEditorState();
    const backgroundColor = schema.marks.backgroundcolor;
    let { ranges } = state.selection;
    let hasColor = false;
    for (let i = 0; !hasColor && i < ranges.length; i++) {
      let { $from, $to } = ranges[i];
      hasColor = state.doc.rangeHasMark($from.pos, $to.pos, backgroundColor);
    }
    for (let i = 0; i < ranges.length; i++) {
      let {$from, $to} = ranges[i]
      if (hasColor) {
        dispatch(state.tr.removeMark($from.pos, $to.pos, backgroundColor));
      }

      // Always run to add the latest font color with the latest state
      state = dryEditorClass.getEditorState();
      dispatch(state.tr.addMark($from.pos, $to.pos, backgroundColor.create(attrs)));
    }
  },
  [EDITOR_COMMAND_NAME_MAP.font_family]: (attrs = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.font_family}`);
    const dispatch = dryEditorClass.getDispatch();
    let state = dryEditorClass.getEditorState();
    const fontFamilyMark = schema.marks.fontfamily;
    const { from, to } = state.selection;
    dispatch(state.tr.removeMark(from, to, fontFamilyMark).scrollIntoView());
    if (attrs.fontFamily && attrs.fontFamily !== 'inherit') {
      state = dryEditorClass.getEditorState();
      dispatch(state.tr.addMark(from, to, fontFamilyMark.create(attrs)).scrollIntoView());
    }
  },
  [EDITOR_COMMAND_NAME_MAP.font_size]: (attrs = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.font_size}`);
    const dispatch = dryEditorClass.getDispatch();
    let state = dryEditorClass.getEditorState();
    const fontSizeMark = schema.marks.fontsize;
    const { from, to } = state.selection;
    dispatch(state.tr.removeMark(from, to, fontSizeMark).scrollIntoView());
    if (attrs.fontSize && attrs.fontSize !== 'inherit') {
      state = dryEditorClass.getEditorState();
      dispatch(state.tr.addMark(from, to, fontSizeMark.create(attrs)).scrollIntoView());
    }
  },
  [EDITOR_COMMAND_NAME_MAP.highlight]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.highlight}`);
    return sharedToggleMark(schema.marks.highlight);
  },
  [EDITOR_COMMAND_NAME_MAP.unordered_list]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.unordered_list}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const bulletNode = schema.nodes['bullet_list'];
    const orderedNode = schema.nodes['ordered_list'];
    const listItemNode = schema.nodes['list_item'];

    // Walk up from cursor to find the enclosing list type
    const { $from } = state.selection;
    let currentListType = null;
    for (let d = $from.depth; d > 0; d--) {
      const nodeType = $from.node(d).type;
      if (nodeType === bulletNode || nodeType === orderedNode) {
        currentListType = nodeType;
        break;
      }
    }

    if (currentListType === bulletNode) {
      liftListItem(listItemNode)(dryEditorClass.getEditorState(), dispatch);
    } else if (currentListType === orderedNode) {
      liftListItem(listItemNode)(dryEditorClass.getEditorState(), dispatch);
      wrapInList(bulletNode)(dryEditorClass.getEditorState(), dispatch);
    } else {
      wrapInList(bulletNode)(dryEditorClass.getEditorState(), dispatch);
    }

    return true;
  },
  [EDITOR_COMMAND_NAME_MAP.ordered_list]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.ordered_list}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const bulletNode = schema.nodes['bullet_list'];
    const orderedNode = schema.nodes['ordered_list'];
    const listItemNode = schema.nodes['list_item'];

    // Walk up from cursor to find the enclosing list type
    const { $from } = state.selection;
    let currentListType = null;
    for (let d = $from.depth; d > 0; d--) {
      const nodeType = $from.node(d).type;
      if (nodeType === bulletNode || nodeType === orderedNode) {
        currentListType = nodeType;
        break;
      }
    }

    if (currentListType === orderedNode) {
      liftListItem(listItemNode)(dryEditorClass.getEditorState(), dispatch);
    } else if (currentListType === bulletNode) {
      liftListItem(listItemNode)(dryEditorClass.getEditorState(), dispatch);
      wrapInList(orderedNode)(dryEditorClass.getEditorState(), dispatch);
    } else {
      wrapInList(orderedNode)(dryEditorClass.getEditorState(), dispatch);
    }

    return true;
  },
  [EDITOR_COMMAND_NAME_MAP.indent_list]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.indent_list}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    return sinkListItem(schema.nodes['list_item'])(state, dispatch) ||
           sinkListItem(schema.nodes['check_list_item'])(state, dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.outdent_list]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.outdent_list}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    return liftListItem(schema.nodes['list_item'])(state, dispatch) ||
           liftListItem(schema.nodes['check_list_item'])(state, dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.enter_list_item]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.enter_list_item}`);
    const dispatch = dryEditorClass.getDispatch();
    const listNode = schema.nodes['list_item'];
    return splitListItem(listNode)(dryEditorClass.getEditorState(), dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.check_list]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.check_list}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const checkListType = schema.nodes['check_list'];
    const checkListItemType = schema.nodes['check_list_item'];

    const { $from } = state.selection;
    let inCheckList = false;
    for (let d = $from.depth; d > 0; d--) {
      if ($from.node(d).type === checkListType) {
        inCheckList = true;
        break;
      }
    }

    if (inCheckList) {
      liftListItem(checkListItemType)(dryEditorClass.getEditorState(), dispatch);
    } else {
      wrapInList(checkListType)(dryEditorClass.getEditorState(), dispatch);
    }
    return true;
  },
  [EDITOR_COMMAND_NAME_MAP.enter_check_list_item]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.enter_check_list_item}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const checkListItemType = schema.nodes['check_list_item'];

    // Wrap dispatch: splitListItem copies attrs to the new item, so a checked
    // item would produce a checked new item. Clear it after the split.
    const wrappedDispatch = (tr) => {
      const $from = tr.selection.$from;
      for (let depth = $from.depth; depth >= 0; depth--) {
        const node = $from.node(depth);
        if (node.type === checkListItemType) {
          if (node.attrs.checked) {
            tr = tr.setNodeMarkup($from.before(depth), null, { checked: false });
          }
          break;
        }
      }
      dispatch(tr);
    };

    return splitListItem(checkListItemType)(state, wrappedDispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.checkbox]: (options = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.checkbox}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const nodeItem = schema.nodes['checkbox'];

    // replaceSelectionWith leaves a NodeSelection on the inserted node because
    // checkbox has selectable:true — the text cursor disappears. Convert it to
    // a TextSelection right after the checkbox so the user can type immediately.
    const tr = state.tr.replaceSelectionWith(nodeItem.create(options));
    return dispatch(tr.setSelection(TextSelection.create(tr.doc, tr.selection.to)).scrollIntoView());
  },
  [EDITOR_COMMAND_NAME_MAP.heading]: (options = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.heading}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const headingType = schema.nodes.heading;
    const paragraphType = schema.nodes.paragraph;

    // level 0 means convert back to paragraph
    if (!options.level) {
      return setBlockType(paragraphType)(state, dispatch);
    }

    // If already this heading level, toggle back to paragraph
    const { $from } = state.selection;
    const node = $from.parent;
    if (node.type === headingType && node.attrs.level === options.level) {
      return setBlockType(paragraphType)(state, dispatch);
    }

    return setBlockType(headingType, options)(state, dispatch);
  },
  [EDITOR_COMMAND_NAME_MAP.horizontal_rule]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.horizontal_rule}`);
    createNodeElement({node: 'horizontal_rule'});
  },
  [EDITOR_COMMAND_NAME_MAP.hard_break]: (attrs = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.hard_break}`);
    createNodeElement({node: 'hard_break'});
  },
  [EDITOR_COMMAND_NAME_MAP.code_block]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.code_block}`);
    createNodeElement({node: 'code_block'});
  },
  [EDITOR_COMMAND_NAME_MAP.image]: (options = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.image}`);
    createNodeElement({node: 'image', options});
  },
  [EDITOR_COMMAND_NAME_MAP.link]: (options = {}) => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.link}`);
    const { href, title = null } = options;
    if (!href) return false;

    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const anchorMark = schema.marks.anchor;
    const { selection } = state;
    const { $from, $to, empty } = selection;

    let from, to;

    if (empty) {
      const range = getMarkRange($from, anchorMark);
      if (!range) return false;
      from = range.from;
      to = range.to;
    } else {
      from = $from.pos;
      to = $to.pos;
    }

    dispatch(
      state.tr
        .removeMark(from, to, anchorMark)
        .addMark(from, to, anchorMark.create({ href, title }))
        .scrollIntoView()
    );
    return true;
  },
  [EDITOR_COMMAND_NAME_MAP.unlink]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.unlink}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const anchorMark = schema.marks.anchor;
    const { selection } = state;
    const { $from, $to, empty } = selection;

    let from, to;

    if (empty) {
      const range = getMarkRange($from, anchorMark);
      if (!range) return false;
      from = range.from;
      to = range.to;
    } else {
      from = $from.pos;
      to = $to.pos;
    }

    dispatch(state.tr.removeMark(from, to, anchorMark).scrollIntoView());
    return true;
  },
  [EDITOR_COMMAND_NAME_MAP.undo]: () => {
    return undo(dryEditorClass.getEditorState(), dryEditorClass.getDispatch());
  },
  [EDITOR_COMMAND_NAME_MAP.redo]: () => {
    return redo(dryEditorClass.getEditorState(), dryEditorClass.getDispatch());
  },
  [EDITOR_COMMAND_NAME_MAP.clear_all]: () => {
    logger.info(`editorCommands - ${EDITOR_COMMAND_NAME_MAP.clear_all}`);
    const dispatch = dryEditorClass.getDispatch();
    const state = dryEditorClass.getEditorState();
    const { selection } = state;
    const from = selection.empty ? 0 : selection.from;
    const to = selection.empty ? state.doc.content.size : selection.to;
    // removeMark with no mark type removes all marks in the range
    dispatch(state.tr.removeMark(from, to).scrollIntoView());
  },
};

const availableCommands = Object.keys(editorCommands);

// export default editorCommands;
export {
  editorCommands,
  availableCommands,
};
